import { defineStore } from 'pinia';
import { useConnectionStore } from '@/stores/connection';
import type { Query, Mutation, SaveWhitelabelConfigMutationInput } from '@/generated/graphql';
import * as Whitelabel from '@/types/Whitelabel';
import type RequestResult from '@/types/RequestResult';
import * as apolloCore from '@apollo/client/core';

const { gql } = apolloCore;

const WHITELABEL_DETAILS_FRAGMENT = gql`
  fragment WhitelabelDetails on WhitelabelConfig {
    id
    domain
    name
    landingUrl
    fromEmail
    stripeApiKey
    stripePublishableKey
    attachment {
      id
      name
      url
    }
  }
`;

const ALL_WHITELATBELS_QUERY = gql`
  query allWhitelabels {
    app {
      whitelabelConfigs {
        ...WhitelabelDetails
      }
    }
  }
  ${WHITELABEL_DETAILS_FRAGMENT}
`;

const SAVE_WHITELABEL_MUTATION = gql`
  mutation saveWhitelabelMutation($input: SaveWhitelabelConfigMutationInput!) {
    app {
      saveWhitelabelConfig(input: $input) {
        clientMutationId
        errors
        config {
          ...WhitelabelDetails
        }
      }
    }
  }
  ${WHITELABEL_DETAILS_FRAGMENT}
`;

const DELETE_WHITELABEL_MUTATION = gql`
  mutation deleteWhitelabelMutation($input: DeleteWhitelabelConfigMutationInput!) {
    app {
      deleteWhitelabelConfig(input: $input) {
        clientMutationId
        errors
      }
    }
  }
`;

interface WhitelabelState {
  whitelabels: Whitelabel.WhitelabelConfig[];
  loading: boolean;
}

export const useWhitelabelStore = defineStore('whitelabels', {
  state: (): WhitelabelState => ({
    whitelabels: [],
    loading: true,
  }),

  actions: {
    async fetchAllWhitelabels() {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.loading = true;
      try {
        const { data } = await apiClient.query<Query>({
          query: ALL_WHITELATBELS_QUERY,
          fetchPolicy: 'network-only',
        });
        this.whitelabels = (data.app?.whitelabelConfigs || []).map(Whitelabel.fromGql);
      } finally {
        this.loading = false;
      }
    },

    async saveWhitelabel(
      payload: SaveWhitelabelConfigMutationInput,
    ): Promise<RequestResult<Whitelabel.WhitelabelConfig>> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.loading = false;

      delete payload.config['stripePublishableKey'];
      delete payload.config['stripeApiKey'];

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: SAVE_WHITELABEL_MUTATION,
          variables: { input: payload },
        });
        if (data?.app?.saveWhitelabelConfig?.errors) {
          return { errors: data?.app.saveWhitelabelConfig.errors };
        }
        if (data?.app?.saveWhitelabelConfig?.config) {
          return { result: Whitelabel.fromGql(data?.app?.saveWhitelabelConfig?.config) };
        }
        return {};
      } finally {
        this.loading = false;
      }
    },

    async deleteWhitelabel(id: number) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.loading = true;
      try {
        await apiClient.mutate<Mutation>({
          mutation: DELETE_WHITELABEL_MUTATION,
          variables: {
            input: { id },
          },
        });
      } finally {
        this.loading = false;
      }
    },
  },
});
